import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdTaskDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { Container, Table } from '@/js/libs/ui/src/V2';

import TableHeader from 'src/components/TableHeaderV2';
import { OrderDirection, useSortAndOrderState } from 'src/hooks/useSortAndOrderState';
import { useTasks } from '../../../hooks';
import type { ColumnName } from '../../../types';
import { TaskTableStatus } from '../../../types';
import { TableFilters } from '../../TableFilters';
import { TableTabs } from '../../TableTabs';
import { TaskRow } from '../../TaskRow';
import { RestoreModal } from './components';
import { useColumns } from './useColumns';

export const Archived: FC = () => {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard', 'adTags']);
  const { sortProps, sortParameters } = useSortAndOrderState<ColumnName>('createdDate', OrderDirection.DESC);
  const { pagination, tasks, tasksLoading, refetchTasks, info } = useTasks({
    status: TaskTableStatus.ARCHIVED,
    sortParameters,
  });
  const columns = useColumns(t);

  const restoreModal = useModalState<IdTaskDto>();

  return (
    <>
      {restoreModal.isMounted && <RestoreModal state={restoreModal} reloadTasks={refetchTasks} />}
      <Container fluid>
        <Table.Wrapper data-testid='tasks-table-archived'>
          <TableTabs />
          <TableFilters info={info} pagination={pagination} sortProps={sortProps} columns={columns} isLoading={tasksLoading} />
          <Table>
            {(tasksLoading || tasks.length > 0) && <TableHeader getSortProps={sortProps} columns={columns} />}
            {!tasksLoading &&
              tasks.map((task) => <TaskRow key={task.id?.toString()} item={task} columns={columns} restoreModalState={restoreModal} />)}
            {tasksLoading && <Table.Loader rows={pagination.perPage} columnsLength={columns.length} />}
          </Table>
          {!tasksLoading && tasks.length === 0 && <Table.NoData message={t('common:THERE_ARE_NO_ENTRIES')} />}
          <Table.Pagination info={info} pagination={pagination} isLoading={tasksLoading} />
        </Table.Wrapper>
      </Container>
    </>
  );
};
